import { PaletteOptions, Theme, ThemeOptions } from '@mui/material';
import {
  BreakpointsOptions,
  Direction,
  Interpolation,
  ShapeOptions,
  SpacingOptions,
} from '@mui/system';
import { CSSProperties } from 'react';
import { RaThemeOptions } from 'react-admin';

// --- CONSTANTS REFERENCED IN THEME FILES ---
type DefinedCSSPropertiesColorBloxColors = Exclude<CSSProperties['color'], undefined>;
type BloxColorKeys =
  | 'white'
  | 'offwhite'
  | 'midlightgray'
  | 'gray'
  | 'middarkgray'
  | 'darkgray'
  | 'medgray'
  | 'gray'
  | 'lightgray'
  | 'bluegray'
  | 'black'
  | 'offblack'
  | 'red'
  | 'darkModeRed'
  | 'orange'
  | 'lightYellow'
  | 'yellow'
  | 'darkYellow'
  | 'green'
  | 'blue'
  | 'darkModeBlue'
  | 'iceblue'
  | 'logoblue'
  | 'indigo'
  | 'purple'
  | 'violet'
  | 'gray1'
  | 'gray2'
  | 'gray3'
  | 'gray4'
  | 'gray5'
  | 'gray6'
  | 'link'
  | 'widgetGray';

export type BloxColorsType = {
  [key in BloxColorKeys]: DefinedCSSPropertiesColorBloxColors;
};

type BloxIconKeys =
  | 'alink'
  | 'article'
  | 'audio'
  | 'batchImport'
  | 'collection'
  | 'cover'
  | 'file'
  | 'form'
  | 'html'
  | 'iframe'
  | 'image'
  | 'import'
  | 'metadata'
  | 'pdf'
  | 'poll'
  | 'table'
  | 'video'
  | 'youtube'
  | 'zip'
  | 'leftnavheader'
  | 'relcontent'
  | 'default';

export type BloxIconColorsType = {
  [key in BloxIconKeys]: DefinedCSSPropertiesColorBloxColors;
};

export type BloxValuesType = {
  darkenCoefficient: number;
  lightenCoefficient: number;
  appBarHeight: number;
  /** defaultBorderRadius / 2 */
  halfBorderRadius: number;
  /** defaultBorderRadius / 4 */
  quarterBorderRadius: number;
  /** 0.625rem - 10px @ standard 16px base browser fontsize */
  microFontSizeRem: string;
  /** 0.75rem - 12px @ standard 16px base browser fontsize */
  tinyFontSizeRem: string;
  /** 0.875rem - 14px @ standard 16px base browser fontsize */
  smallFontSizeRem: string;
  /** 1rem - 16px @ standard 16px base browser fontsize */
  regularFontSizeRem: string;
  /** 1.125rem - 18px @ standard 16px base browser fontsize */
  xlFontSizeRem: string;
  /** 1.25rem- 20px @ standard 16px base browser fontsize */
  xxlFontSizeRem: string;
  /** 1.5rem - 24px @ standard 16px base browser fontsize */
  xxxlFontSizeRem: string;
  /** 2rem - 32px @ standard 16px base browser fontsize */
  megaFontSizeRem: string;
  /** zIndex: 9999 (eg: logout/reauth, full interaction blocks) */
  zIndexMax: number;
  /** zIndex: 5000 (eg below chat, higher than most) */
  zIndexHigh: number;
  /** zIndex: 7000 (eg: copilot kit button and chat window) */
  zIndexAiChat: number;
  /** 300 */
  fontWeightLight: number;
  /** 400 */
  fontWeightRegular: number;
  /** 500 */
  fontWeightMedium: number;
  /** 700 */
  fontWeightBold: number;
  /** 20px - some compact areas use specific 20px */
  iconSizeTwenty: number;
  /** 16px */
  iconSizeSmall: number;
  /** 24px - standard size - icons seem sharpest at 24px / factors of 12 */
  iconSizeRegular: number;
  /** 36px */
  iconSizeLarge: number;
};

type ExtendedComponents = ThemeOptions['components'] & RaThemeOptions['components'];
export interface CustomThemeOptions extends ThemeOptions {
  components?: ExtendedComponents;
  blox: {
    colors: BloxColorsType;
    values: BloxValuesType;
    iconColors: BloxIconColorsType;
    table: BloxTableColorsType;
    chart: BloxChartColors;
    marketing: BloxMarketingColorsType;
    revisions: TBloxRevisionsColors;
  };
}

export const defaultBorderRadius = 4;

export const shape: ShapeOptions = {
  borderRadius: defaultBorderRadius,
};

export const fontWeightLight = 300;
export const fontWeightRegular = 400;
export const fontWeightMedium = 500;
export const fontWeightBold = 700;

// Common values we can reference in one place via theme.blox.values.*
export const bloxValues: BloxValuesType = {
  appBarHeight: 64,
  darkenCoefficient: 0.25,
  lightenCoefficient: 0.25,
  zIndexMax: 9999,
  zIndexHigh: 5000,
  zIndexAiChat: 7000,
  halfBorderRadius: defaultBorderRadius / 2,
  quarterBorderRadius: defaultBorderRadius / 4,
  microFontSizeRem: '0.625rem',
  tinyFontSizeRem: '0.75rem',
  smallFontSizeRem: '0.875rem',
  regularFontSizeRem: '1rem',
  xlFontSizeRem: '1.125rem',
  xxlFontSizeRem: '1.25rem',
  xxxlFontSizeRem: '1.5rem',
  megaFontSizeRem: '2rem',
  fontWeightLight: fontWeightLight,
  fontWeightRegular: fontWeightRegular,
  fontWeightMedium: fontWeightMedium,
  fontWeightBold: fontWeightBold,
  iconSizeTwenty: 20,
  iconSizeSmall: 16,
  iconSizeRegular: 24,
  iconSizeLarge: 36,
};

// Blox Colors - these direct values apply if accessing via theme.blox.colors.*
// Gray shades spaced evenly between offwhite and offblack
// https://coolors.co/gradient-palette/fdfdfd-0f0f0f?number=7
export const bloxColors: BloxColorsType = {
  white: '#FFFFFF',
  offwhite: '#FDFDFD',
  lightgray: '#D5D5D5',
  midlightgray: '#AEAEAE',
  gray: '#868686',
  middarkgray: '#5E5E5E',
  medgray: '#4D4D4D',
  darkgray: '#373737',
  offblack: '#0F0F0F',
  black: '#000000',
  bluegray: '#344563',
  red: '#CB3300',
  darkModeRed: '#DA420F', // slightly lighter error red for dark mode
  orange: '#D5581A',
  lightYellow: '#FFF2CC',
  yellow: '#E2B93B',
  darkYellow: '#AE8B00', // slightly darker for text contrast
  green: '#38B349',
  blue: '#1879BD',
  darkModeBlue: '#2A8BCF', // slightly lighter primary blue for dark mode
  iceblue: '#D2E5F2',
  logoblue: '#00A3E1',
  indigo: '#4938B3',
  purple: '#8338AA',
  violet: '#B338A2',
  gray1: '#4F5B79',
  gray2: '#7F8FA4',
  gray3: '#979797',
  gray4: '#F8F8F8',
  gray5: '#f2f5f9', // Soft blue-gray.
  gray6: '#eaf0f7', // Soft blue-gray hover.
  link: '#1778BC',
  widgetGray: '#ebebeb',
};

type TScrollbarStyles = {
  width: string;
  height: string;
  track: { background: string; hover: { background: string } };
  corner: { background: string };
  button: { display: string };
  thumb: { background: string; hover: { background: string } };
};

export const scrollbarStyles: TScrollbarStyles = {
  width: '6px',
  height: '6px',
  track: {
    background: 'transparent',
    hover: { background: 'light-dark(#0000000D,#FFFFFF1A)' },
  },
  corner: { background: 'transparent' },
  button: { display: 'none' },
  thumb: {
    background: 'light-dark(#0000001A,#FFFFFF33)',
    hover: { background: 'light-dark(#00000033,#FFFFFF66)' },
  },
};

/**
 * Place any global styles here that can't be added to the theme overrides.
 * eg. to customize the browser's scrollbar, selection, html elements, etc.
 * can think of this as the classic index.css file for index.html
 */

export const globalStyles: Interpolation<Theme> = {
  '::selection': {
    background: bloxColors.logoblue,
  },
  '::-moz-selection': {
    background: bloxColors.logoblue,
  },
  // Global scrollbar overrides
  '*::-webkit-scrollbar': {
    width: scrollbarStyles.width,
    height: scrollbarStyles.height,
  },
  '*::-webkit-scrollbar-track': {
    background: scrollbarStyles.track.background,
  },
  '*::-webkit-scrollbar-track:hover': {
    background: scrollbarStyles.track.hover.background,
  },
  '*::-webkit-scrollbar-corner': {
    background: scrollbarStyles.corner.background,
  },
  '*::-webkit-scrollbar-button': {
    display: scrollbarStyles.button.display,
  },
  '*::-webkit-scrollbar-thumb': {
    background: scrollbarStyles.thumb.background,
  },
  '*::-webkit-scrollbar-thumb:hover': {
    background: scrollbarStyles.thumb.hover.background,
  },

  'html, body': {
    display: 'contents', // Go straight to MainLayout grid as first css box rendered, may also help accessibility tree.
    height: '100%',
    width: '100%',
    minHeight: '100%',
    minWidth: '100%',
    margin: '0',
    padding: '0',
    color: 'inherit',
    fontWeight: 'normal',
    letterSpacing: 'normal',
    fontFeatureSettings: 'normal',
    textWrap: 'pretty',
    textRendering: 'optimizeLegibility',
  },

  // See note in MainLayout.tsx about these root targets..
  '#root': {
    display: 'inherit', // Inherit from html/body (contents) - if needed, can also inherit max-h/w and w/h here.
  },
  // Tries to target the ScopedCssBaseline inside RA's <AdminUI>
  '#root > .MuiScopedCssBaseline-root': {
    display: 'inherit',
    fontSize: 'inherit',
    fontWeight: 'inherit',
    letterSpacing: 'inherit', // Overriding the scoped-css-basline setting this to a strange value...
  },
  // More notes on this in MainLayout.tsx - tries to target the plain RA div at:
  // AdminUI > CoreAdminUI > CoreAdminRoutes >  return element={ <div> ... <Layout>... }
  // Note: this seems fixed in react-admin v5+, so can be removed in future
  // then MainLayout can be targeted directly and not need 'contents' above...
  '#root > .MuiScopedCssBaseline-root > div:first-of-type': {
    display: 'inherit',
  },
};

// Blox Icon Colors - these direct values apply if accessing via theme.blox.iconColors.*
export const bloxIconColors: BloxIconColorsType = {
  alink: '#006dc3',
  article: '#295497',
  audio: '#555ba4',
  batchImport: '#000000',
  collection: '#d14424',
  cover: '#dc4405',
  file: '#37aec1',
  form: '#000000',
  html: '#000000',
  iframe: '#CC2D79',
  image: '#1ea261',
  import: '#000000',
  metadata: '#000000',
  pdf: '#a33539',
  poll: '#68ac73',
  table: '#1f7244',
  video: '#f70000',
  youtube: '#B2071D',
  zip: '#000000',
  relcontent: '#4F5B79',
  // Application branding colors
  leftnavheader: '#3137BF',
  // Default
  default: bloxColors.gray,
};

export const bloxMarketingColors = {
  primary: '#02528a',
  secondary: '#a7d400',
  tertiary: '#5f249f',
  warning: bloxColors.blue,
  error: '#e4002b',
};
export type BloxMarketingColorsType = typeof bloxMarketingColors;

export const bloxTableColors = {
  border: '#313E4D',
  hover: '#293541',
  link: bloxIconColors.alink,
  text: bloxColors.bluegray,
  trendingDown: bloxIconColors.pdf,
  trendingFire: '#f57c00',
  trendingUp: bloxIconColors.image,
  legendFire: '#fac895',

  // Trending colors
  neutral: bloxColors.white,
  primary: bloxColors.blue,
  secondary: bloxIconColors.image,
  error: bloxIconColors.youtube,
  warning: '#f57c00',
  info: bloxIconColors.audio,
};
export type BloxTableColorsType = typeof bloxTableColors;

// Listed in priority for pie chart.
export const bloxChartColors = {
  blue: bloxIconColors.article,
  liteBlue: bloxIconColors.file,
  gray: '#3F4D5E',
  green: bloxIconColors.table,
  liteGreen: bloxIconColors.image,
  purple: bloxIconColors.audio,
  red: bloxIconColors.pdf,
  magenta: bloxIconColors.iframe,
  orange: bloxIconColors.collection,
};
export type BloxChartColors = typeof bloxChartColors;

export const bloxRevisionsColors = {
  green: '#DCF1DA',
  darkGreen: '#9DD897',
  lightRed: '#FBE0E0',
  darkRed: bloxIconColors.pdf,
  red: '#F6B6B6',
  opaqueBlue: '#99D6EA33',
  lightBlue: '#5FE0FD33',
  blue: '#99D6EA',
  text: bloxColors.offblack,
  lightYellow: '#FFC10733',
  yellow: '#FFC107',
  darkYellow: '#FFC10799',
};
export type TBloxRevisionsColors = typeof bloxRevisionsColors;

// Brand Colors - universally defined for the whole site
// Access via theme.palette.brands.facebook.main, etc.
export const bloxBrandColors = {
  facebook: {
    main: '#1877F2',
    light: '#4293F5',
    dark: '#1266D6',
    contrastText: '#FFFFFF',
  },
} as const;

export type BloxBrandColorsType = typeof bloxBrandColors;

export const bloxPoliticsColors = {
  liberal: '#083A90', // dnc primary blue
  leans_left: '#819AC5', // 50% blend neutral
  neutral: '#FAFAFA', // neutral white
  leans_right: '#F18B8F', // 50% blend neutral
  conservative: '#E81B23', // gop primary red
};
export type TBloxPoliticsColors = typeof bloxPoliticsColors;

// --- BASE THEME CONFIG ---
export const breakpoints: BreakpointsOptions = {
  values: {
    xs: 0,
    sm: 600,
    md: 900,
    lg: 1200,
    xl: 1536,
  },
  unit: 'px',
  step: 5,
};

export const direction: Direction = 'ltr';

export const spacing: SpacingOptions = 8;

export const lineHeight = 'normal';

export const typography = {
  htmlFontSize: 16, // base font size for the html document
  fontSize: 16, // default font size for components
  fontFamily:
    'Roboto,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI","Helvetica Neue",Arial,sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol"',
  fontWeightLight: fontWeightLight,
  fontWeightRegular: fontWeightRegular,
  fontWeightMedium: fontWeightMedium,
  fontWeightBold: fontWeightBold,
  allVariants: {
    lineHeight: lineHeight,
    letterSpacing: 'normal',
    textWrap: 'pretty',
    textRendering: 'optimizeLegibility',
    fontFeatureSettings: 'normal',
  },
  h1: {
    fontSize: bloxValues.xxlFontSizeRem,
    fontWeight: fontWeightMedium,
  },
  h2: {
    fontSize: bloxValues.regularFontSizeRem,
    fontWeight: fontWeightMedium,
  },
  subtitle1: {
    fontSize: bloxValues.smallFontSizeRem,
    fontWeight: fontWeightRegular,
    color: bloxColors.gray,
  },
  // Custom Typography variants (https://mui.com/material-ui/customization/typography/#adding-amp-disabling-variants).
  // Update /src/themes/theme.d.ts when adding new variants.
  heading: {
    fontWeight: fontWeightBold,
    fontSize: bloxValues.xxlFontSizeRem,
    marginBottom: '1rem',
  },
  // Form labels.
  label: {
    fontWeight: fontWeightBold,
  },
};

// --- SPECIFIC LIGHT/DARK BASE THEME CONFIG / OVERRIDES ---
export const lightPalette: PaletteOptions = {
  mode: 'light',
  contrastThreshold: 4,
  tonalOffset: 0.2,
  brands: bloxBrandColors,
  common: {
    black: bloxColors.black,
    white: bloxColors.white,
  },
  background: {
    default: bloxColors.white,
    paper: bloxColors.offwhite,
  },
  text: {
    primary: bloxColors.black,
    secondary: bloxColors.middarkgray,
    disabled: bloxColors.gray,
  },
  divider: bloxColors.lightgray,
  primary: {
    main: bloxColors.blue,
    contrastText: bloxColors.white,
  },
  secondary: {
    main: bloxColors.darkYellow,
  },
  error: {
    main: bloxColors.red,
    contrastText: bloxColors.white,
  },
  warning: {
    main: bloxColors.orange,
  },
  info: {
    main: bloxColors.gray,
    // Improve Info contrast and match other styles (dark and light mode)
    contrastText: bloxColors.white,
  },
  success: {
    main: bloxColors.green,
    contrastText: bloxColors.white,
  },
};

export const darkPalette: PaletteOptions = {
  mode: 'dark',
  contrastThreshold: 4,
  tonalOffset: 0.2,
  brands: bloxBrandColors,
  common: {
    black: bloxColors.black,
    white: bloxColors.white,
  },
  background: {
    default: bloxColors.black,
    paper: bloxColors.offblack,
  },
  text: {
    primary: bloxColors.white,
    secondary: bloxColors.gray,
    disabled: bloxColors.middarkgray,
  },
  divider: bloxColors.darkgray,
  primary: {
    main: bloxColors.darkModeBlue,
    contrastText: bloxColors.white,
  },
  secondary: {
    main: bloxColors.darkYellow,
  },
  error: {
    main: bloxColors.darkModeRed,
    contrastText: bloxColors.white,
  },
  warning: {
    main: bloxColors.orange,
  },
  info: {
    main: bloxColors.gray,
    contrastText: bloxColors.white,
  },
  success: {
    main: bloxColors.green,
    contrastText: bloxColors.white,
  },
};
